import React, { createContext, useContext, useEffect, useState } from 'react';
import { Analytics, AnalyticsBrowser } from '@segment/analytics-next';
import { getAnonymousId } from '../utils/analytics';

interface AnalyticsContextType {
  analytics: Analytics | null;
  isLoaded: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({ 
  analytics: null,
  isLoaded: false
});

export const useAnalytics = () => useContext(AnalyticsContext);

interface AnalyticsProviderProps {
  children: React.ReactNode;
  writeKey: string;
}

export function AnalyticsProvider({ children, writeKey }: AnalyticsProviderProps) {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const initAnalytics = async () => {
      console.log('Initializing Segment analytics with write key:', writeKey ? `${writeKey.substring(0, 4)}...` : 'not provided');
      
      if (!writeKey) {
        console.warn('Segment write key is not provided. Analytics will not be initialized.');
        return;
      }

      try {
        console.log('Loading Segment analytics...');
        // Initialize Segment
        const [analyticsInstance] = await AnalyticsBrowser.load({ writeKey });
        console.log('Segment analytics loaded successfully');
        
        // Set anonymous ID
        const anonymousId = getAnonymousId();
        analyticsInstance.setAnonymousId(anonymousId);
        console.log('Anonymous ID set:', anonymousId);
        
        // Add context to all events
        analyticsInstance.addSourceMiddleware(({ payload, next }) => {
          const { integrations = {}, ...rest } = payload;
          
          // Add app context to all events
          const updatedPayload = {
            ...rest,
            integrations,
            context: {
              ...payload.context,
              app: {
                name: 'Sticker Designer',
                version: import.meta.env.VITE_APP_VERSION || '1.0.0',
                environment: import.meta.env.MODE
              }
            }
          };
          
          next(updatedPayload);
        });

        // Track page view
        analyticsInstance.page();
        console.log('Initial page view tracked');
        
        setAnalytics(analyticsInstance);
        setIsLoaded(true);
      } catch (error) {
        console.error('Failed to initialize Segment analytics:', error);
      }
    };

    initAnalytics();

    return () => {
      // Cleanup if needed
    };
  }, [writeKey]);

  return (
    <AnalyticsContext.Provider value={{ analytics, isLoaded }}>
      {children}
    </AnalyticsContext.Provider>
  );
}
